'use client';

import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@/components/ui/dialog';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import type { ProjectDoc } from '@/types';
import { formatDate, isValid } from 'date-fns';
import { DATE_FORMAT_WITH_TIME } from '@/constants';
import {
    Clock,
    User,
    Download,
    Pencil,
    Trash2,
    ArrowLeft
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DocComments } from './doc-comments';
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface DocViewModalProps {
    doc?: ProjectDoc;
    isOpen: boolean;
    onClose: () => void;
    canManage: boolean;
    onEdit?: (doc: ProjectDoc) => void;
    onDelete?: (docId: number) => Promise<void>;
    isExternal?: boolean;
}

const safeFormatDate = (dateStr: string | undefined | null, formatStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (!isValid(date)) return 'N/A';
    return formatDate(date, formatStr);
};

export const DocViewModal = ({
    doc,
    isOpen,
    onClose,
    canManage,
    onEdit,
    onDelete,
    isExternal = false,
}: DocViewModalProps) => {
    if (!doc) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChangeHandler}>
            <DialogContent className="w-screen sm:max-w-6xl h-[90vh] overflow-hidden rounded-3xl shadow-2xl bg-background/95 backdrop-blur-lg p-0">

                {/* Header */}
                <div className="relative px-8 xl:px-16 pt-12 pb-10 bg-gradient-to-br from-primary/10 via-transparent to-transparent border-b border-border/20">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="absolute left-6 xl:left-10 top-6 gap-2 hover:bg-primary/10 rounded-full px-4 transition"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>

                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mt-8">
                        <div className="flex-1 space-y-4">
                            <div className="flex flex-wrap gap-3 animate-in fade-in slide-in-from-left duration-700">
                                <Badge variant="outline" className="text-[10px] uppercase font-bold tracking-wider px-3 h-6 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
                                    Project Asset
                                </Badge>
                                <Badge variant="secondary" className="text-[10px] uppercase font-bold tracking-wider px-3 h-6 bg-muted/50 text-muted-foreground border-border/30 backdrop-blur-sm">
                                    {isExternal ? 'EXTERNAL DOC' : 'INTERNAL DOC'}
                                </Badge>
                            </div>

                            <DialogTitle className="font-bold">
                                {doc.title}
                            </DialogTitle>

                            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-foreground/80 font-medium italic animate-in fade-in slide-in-from-bottom duration-1000 delay-200">
                                <span className="flex items-center gap-2.5">
                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center border border-primary/30">
                                        <User className="h-4 w-4 text-primary" />
                                    </div>
                                    {doc.created_by ? `${doc.created_by.first_name} ${doc.created_by.last_name || ''}` : 'Unknown Author'}
                                </span>

                                <span className="flex items-center gap-2.5">
                                    <div className="h-8 w-8 rounded-full bg-muted/20 flex items-center justify-center border border-border/40">
                                        <Clock className="h-4 w-4 text-muted-foreground/70" />
                                    </div>
                                    Updated {safeFormatDate(doc.updated_on, DATE_FORMAT_WITH_TIME)}
                                </span>
                            </div>
                        </div>

                        {canManage && (
                            <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right duration-1000">
                                {doc.attachment && (
                                    <Button
                                        onClick={() => window.open(doc.attachment!, '_blank')}
                                    >
                                        <Download className="h-5 w-5" />
                                        Download
                                    </Button>
                                )}

                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => onEdit?.(doc)}
                                >
                                    <Pencil className="h-6 w-6" />
                                </Button>

                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                        >
                                            <Trash2 className="h-6 w-6" />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="rounded-3xl border-border/10 backdrop-blur-2xl">
                                        <AlertDialogHeader>
                                            <AlertDialogTitle className="text-2xl font-bold italic">Permanently Remove Document?</AlertDialogTitle>
                                            <AlertDialogDescription className="text-base text-muted-foreground/90">
                                                This will purge "{doc.title}" and all its associated discussions. This action is irreversible.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter className="mt-8 gap-4">
                                            <AlertDialogCancel>Abort</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => onDelete?.(doc.id).then(onClose)}
                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 "
                                            >
                                                Purge Assets
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex overflow-hidden">
                    <ScrollArea className="flex-1">
                        <div className="px-8 xl:px-24 py-16 max-w-6xl mx-auto space-y-16">
                            {doc.project_files === 'attachment' ? (
                                <div className="flex flex-col items-center justify-center py-24 rounded-3xl border-2 border-dashed border-border/40 bg-muted/10 animate-in zoom-in duration-500">
                                    <div className="h-32 w-32 rounded-3xl bg-primary/10 flex items-center justify-center mb-8 shadow-xl relative group">
                                        <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all" />
                                        <Download className="h-12 w-12 text-primary relative z-10" />
                                    </div>
                                    <h3 className="text-3xl font-black tracking-tight mb-2">Attached Resource</h3>
                                    <p className="text-lg text-muted-foreground mb-10 max-w-md text-center">
                                        This document is primarily a file attachment. Access the full file below.
                                    </p>
                                    <Button
                                        size="lg"
                                        onClick={() => window.open(doc.attachment!, '_blank')}
                                        className="h-16 px-10 rounded-2xl gap-3 text-lg font-bold shadow-xl shadow-primary/30 hover:scale-105 active:scale-95 transition-all"
                                    >
                                        <Download className="h-6 w-6" />
                                        Download {doc.attachment?.split('/').pop() || 'File'}
                                    </Button>
                                </div>
                            ) : (
                                <ReadOnlyContentDisplay
                                    html={doc.project_files}
                                    className="prose prose-xl prose-stone dark:prose-invert max-w-none
                                        prose-headings:font-black prose-headings:tracking-tight
                                        prose-p:leading-8 prose-p:text-foreground/90 prose-p:text-lg
                                        prose-img:rounded-3xl prose-img:shadow-xl
                                        prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-primary/10 prose-blockquote:px-8 prose-blockquote:py-4 prose-blockquote:rounded-r-2xl
                                        prose-code:bg-muted prose-code:px-2 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none"
                                />
                            )}

                            {/* Comments */}
                            <div className="mt-24">
                                <DocComments docId={doc.id} isExternal={isExternal} />
                            </div>
                        </div>
                    </ScrollArea>
                </div>
            </DialogContent>
        </Dialog>
    );

    function onOpenChangeHandler(open: boolean) {
        if (!open) onClose();
    }
};